import { FC, useEffect, useMemo, useState } from 'react';

import { CircularProgress, styled } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { usePayLinkCart } from '../../../../context/PayLinkCartContext';
import { usePayLinkUser } from '../../../../context/PaylinkUserContext';
import { getAccuredPoint } from '../../../../services/api';
import { IAccurredPointResponse } from '../../../../types/loyalty';

const PointCardBox = styled("div")(({ theme }) => ({
  "& .title": {
    fontSize: "0.875rem",
    fontWeight: 500,
    color: "#150200",
  },

  "& .balance": {
    fontSize: "0.875rem",
    fontWeight: 500,
    color: "#0081D4",
  },

  "& .card": {
    border: "1px solid #E6E6E7",
    borderRadius: "0.75rem",
    padding: "0.875rem 1rem",
    transition: "0.3s all ease-in-out",
    cursor: "pointer",
  },

  "& .card.selected": {
    border: "1px solid #00AE97",
  },

  "& .selector": {
    width: "24px",
    height: "24px",
    borderRadius: "8px",
    border: "2px solid #00AE97",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginTop: "0.1rem",
  },

  "& .selectorInner": {
    width: "12px",
    height: "12px",
    borderRadius: "4px",
    background: "#00AE97",
  },

  "& .inputHolder": {
    "& input": {
      borderWidth: "0 0 0.5px 0",
      borderColor: "#D9D9DC",
      padding: "0.5rem 0",
      transition: "0.3s all ease-in-out",

      "&:focus-visible": {
        borderWidth: "0 0 1px 0",
        borderColor: "#00AE97",
        outline: "none",
        boxShadow: "none",
        backgroundColor: "#fff",
      },

      "&::placeholder": {
        color: "#8F9097",
        fontSize: "0.75rem",
        fontWeight: 500,
      },

      "&:required": {
        borderBottom: "1.5px solid #dc3545",
      },

      "&::-webkit-outer-spin-button, ::-webkit-inner-spin-button": {
        display: "none",
        "-webkit-appearance": "none",
        margin: 0,
      },
    },
  },

  "& .errorMessage": {
    fontWeight: 500,
    fontSize: "0.75rem",
    display: "block",
    color: "#dc3545",
  },
}));

interface IProps {
  isPointChecked: boolean;
  totalAmount: number;
}

type TPointBalanceByMerchant = IAccurredPointResponse & {
  merchantUniqueReferenceNumber: string;
};

export const PointCard: FC<IProps> = ({ isPointChecked, totalAmount }) => {
  const {
    user,
    pointMerchant: selectedPointMerchant,
    redeemedPoints,
    setPointMerchant: setSelectedPointMerchant,
    setRedeemedPoints,
    adminDetails,
  } = usePayLinkUser();
  const { storeProfile } = usePayLinkCart();
  const [pointValue, setPointValue] = useState<number | string>(0);
  const [hasValue, setHasValue] = useState<boolean>(false);
  const [invalidPoint, setInvalidPoint] = useState<{
    isError: boolean;
    errorMessage: string;
  }>({
    isError: false,
    errorMessage: "",
  });

  const { data: pointsData, isLoading } = useQuery({
    queryKey: ["GetAccuredPoint"],
    queryFn: () =>
      getAccuredPoint({
        CustomerUniqueReferenceNumber:
          storeProfile?.customerUniqueReferenceNumber as string,
        ConsumerUniqueReferenceNumber:
          user?.consumerUniqueReferenceNumber as string,
      }),
    enabled:
      !!user?.consumerUniqueReferenceNumber &&
      !!storeProfile?.customerUniqueReferenceNumber,
    retry: 0,
    staleTime: Infinity,
  });

  const pointsByMerchant = useMemo<IAccurredPointResponse[]>(() => {
    const userPoints = pointsData?.data ?? [];
    if (userPoints.length === 0) return [];

    const grouped = userPoints.reduce<Record<string, TPointBalanceByMerchant>>(
      (acc, item) => {
        const merchantUrn = item.merchantUniqueReferenceNumber || "unknown";

        if (!acc[merchantUrn]) {
          acc[merchantUrn] = {
            ...item,
            merchantUniqueReferenceNumber: merchantUrn,
          };
        } else {
          acc[merchantUrn].earnedBalance += item.earnedBalance || 0;
          acc[merchantUrn].spentBalance += item.spentBalance || 0;
        }

        return acc;
      },
      {},
    );

    return Object.values(grouped);
  }, [pointsData]);

  // console.log({ pointsByMerchant });

  // Reset point value when unchecked
  useEffect(() => {
    if (!isPointChecked) {
      setSelectedPointMerchant(null);
      updatePointGlobally(0);
      setHasValue(false);
      setInvalidPoint({ isError: false, errorMessage: "" });
    }
  }, [isPointChecked, setSelectedPointMerchant]);

  const getCurrentPoint = (earnedPoint: number, spentPoint: number) => {
    return (earnedPoint || 0) - (spentPoint || 0);
  };

  const updatePointGlobally = (updatedPoint: number) => {
    setPointValue(updatedPoint === 0 ? "" : updatedPoint);
    setRedeemedPoints(updatedPoint);
  };

  const validatePointMinAndMaxValue = (
    event: React.ChangeEvent<HTMLInputElement>,
    maxNumber: number,
  ) => {
    const value = Number(event.target.value);
    const minValue = 0;
    const maxValue = maxNumber;

    setPointValue(event.target.value);

    if (value < minValue) {
      setInvalidPoint({
        isError: true,
        errorMessage: "Invalid input, enter value above 0 points",
      });
      event.target.value = "";
    } else if (value > maxValue) {
      setInvalidPoint({
        isError: true,
        errorMessage:
          "Invalid input, Points cannot be more than your cart total",
      });
      return;
    } else if (selectedPointMerchant && value > selectedPointMerchant.points) {
      setInvalidPoint({
        isError: true,
        errorMessage: `Insufficient points, enter point less than ${selectedPointMerchant.points}`,
      });
      event.target.value = event.target.value.slice(0, -1);
    } else {
      setInvalidPoint({
        isError: false,
        errorMessage: "",
      });
    }
  };

  const handleSelectMerchantPoint = (
    merchantUrn: string,
  ) => {
    const selected = pointsByMerchant.find(
      (merchantPoint) => merchantPoint.merchantUniqueReferenceNumber === merchantUrn,
    );

    if (selected) {
      setSelectedPointMerchant({
        ...selected,
      });
      setPointValue(0);
      setRedeemedPoints(0);
      setHasValue(false);
      setInvalidPoint({ isError: false, errorMessage: "" });
    }
  };

  if (pointsByMerchant.length === 0) {
    return <p className="text-sm text-py_black-500">No points available.</p>;
  }

  return (
    <PointCardBox>
      {isLoading && (
        <div className="!py-20 flex flex-col items-center justify-center !gap-4">
          <CircularProgress />
          <span className="text-xs lg:text-sm text-py_black-500">
            Loading points...
          </span>
        </div>
      )}
      <div className="!w-full !flex flex-col gap-3">
        {pointsByMerchant.map((merchantPoint, id) => {
          const merchantUrn = merchantPoint.merchantUniqueReferenceNumber;
          const isSelected =
            merchantUrn ===
            selectedPointMerchant?.merchantUniqueReferenceNumber;
          const currentPoints = getCurrentPoint(
            merchantPoint.earnedBalance,
            merchantPoint.spentBalance,
          );

          return (
            <div
              key={id}
              className={`card w-full ${isSelected ? "selected" : ""}`}
              onClick={() => handleSelectMerchantPoint(merchantUrn)}
            >
              <div className="d-flex align-items-center justify-content-between gap-3">
                <div className="inline-flex items-center gap-3">
                  <span className="selector">
                    {isSelected && <span className="selectorInner" />}
                  </span>

                  <h3 className="title mb-0 uppercase font-semibold">
                    {adminDetails?.uniqueReferenceNumber === merchantUrn
                      ? "Paycurra"
                      : "Merchant"}
                  </h3>
                </div>

                <div className="balance">
                  {new Intl.NumberFormat().format(
                    currentPoints - (isSelected ? Number(pointValue) || 0 : 0),
                  )}{" "}
                  points
                </div>
              </div>

              {isSelected && (
                <div className="inputHolder mt-3 ml-auto">
                  <input
                    type={"number"}
                    placeholder="Enter the points you want to redeem"
                    className="w-full"
                    required={isPointChecked}
                    autoFocus={isPointChecked}
                    value={pointValue}
                    onChange={(e) => {
                      e.target.value.length >= 1
                        ? setHasValue(true)
                        : setHasValue(false);
                      validatePointMinAndMaxValue(e, totalAmount);
                      setPointValue(e.target.value);
                      setRedeemedPoints(Number(e.target.value) || 0);
                    }}
                    readOnly={!isPointChecked}
                    pattern="[0-9]"
                  />

                  {invalidPoint.isError && (
                    <small className="errorMessage mt-2">
                      {invalidPoint.errorMessage}
                    </small>
                  )}

                  {isPointChecked && !hasValue && !invalidPoint.isError && (
                    <small className="errorMessage mt-2">
                      Please enter point to apply
                    </small>
                  )}

                  <small className="text-py_black-500 text-xs mt-3 block">
                    Your order total will be adjusted with any value of points you redeem
                  </small>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </PointCardBox>
  );
};
